/**
 * Componente: ContractTemplateVersionHistoryDialog
 * Descrição: Modal de histórico de versões de um template de contrato.
 *            Exibe a lista de snapshots registrados e permite comparar
 *            visualmente a versão selecionada com a versão atual.
 *
 * RELEASE: v2.0.0 - 2026-02-19
 * Funcionalidades:
 * - Listar versões ordenadas do mais recente ao mais antigo
 * - Tab "Preview": visualização side-by-side dos HTMLs renderizados
 * - Tab "Diferenças": diff de TEXTO PURO (sem tags HTML) com destaque verde/vermelho
 * - Linhas de contexto colapsáveis (3 linhas antes/depois de cada bloco de mudança)
 * - Contador de adições/remoções no cabeçalho
 * - Scroll sincronizado no preview side-by-side
 * - Estado elegante quando versões são idênticas ou não há histórico
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { History, ChevronDown, Plus, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useContractTemplateVersions } from '@/hooks/useContractTemplateVersions';

import type { ContractTemplateVersion } from '@/types/contracts';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('ContractTemplateVersionHistoryDialog');

/** Número de linhas de contexto a exibir ao redor de cada bloco de mudança */
const CONTEXT_LINES = 3;

// ============================================================================
// Tipos
// ============================================================================

interface Props {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para fechar o modal */
  onClose: () => void;
  /** ID do template cujo histórico será exibido */
  templateId: string;
  /** Nome do template para exibição no título */
  templateName: string;
}

/** Tipo de linha no diff */
type DiffLineType = 'equal' | 'added' | 'removed';

interface DiffLine {
  type: DiffLineType;
  text: string;
}

/** Bloco de linhas de contexto (colapsado ou expandido) */
interface ContextBlock {
  kind: 'context';
  lines: string[];
  expanded: boolean;
}

/** Bloco de mudança (linhas adicionadas/removidas) */
interface ChangeBlock {
  kind: 'change';
  removed: string[];
  added: string[];
}

/** Bloco renderizável no diff final */
type DiffBlock = ContextBlock | ChangeBlock;

// ============================================================================
// Utilitários
// ============================================================================

/**
 * Extrai texto puro de HTML usando DOMParser.
 * Elimina todas as tags antes de calcular o diff,
 * para que o usuário veja frases reais, não código HTML.
 *
 * @param html - String HTML a converter
 * @returns Texto plano, linha a linha
 */
function htmlToPlainText(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Substitui <br> e blocos por nova linha antes de pegar textContent
  const text = doc.body.textContent || '';
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .join('\n');
}

/**
 * Extrai segmentos semânticos do HTML para o diff visual side-by-side.
 * Em vez de split por \n, usa DOMParser para extrair cada bloco de texto
 * (p, li, h1-h6, td, th) como um segmento independente.
 * Isso garante alinhamento correto entre os dois lados do diff.
 *
 * @param html - String HTML a processar
 * @returns Array de strings com o texto de cada elemento semântico
 */
function extractSegments(html: string): string[] {
  if (!html) return [''];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const segments: string[] = [];
  // Itera todos os elementos de bloco semântico do body
  doc.body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, td, th').forEach((el) => {
    const text = el.textContent?.trim();
    if (text) segments.push(text);
  });
  // Fallback: se nenhum bloco semântico, retorna o texto todo como um único segmento
  if (segments.length === 0) {
    const raw = doc.body.textContent?.trim() || '';
    if (raw) segments.push(raw);
  }
  return segments;
}

/**
 * Calcula diff de palavras entre duas linhas para highlight intra-linha.
 * Usa LCS simples sobre palavras para identificar quais mudaram.
 * Retorna as palavras de cada lado marcadas como changed=true quando diferentes.
 *
 * @param oldLine - Texto da linha antiga
 * @param newLine - Texto da linha nova
 * @returns Objeto com arrays de tokens para cada lado, com flag changed
 */
function wordDiff(
  oldLine: string,
  newLine: string,
): {
  old: Array<{ text: string; changed: boolean }>;
  new: Array<{ text: string; changed: boolean }>;
} {
  // Tokeniza preservando espaços
  const tokenize = (s: string) => s.split(/(\s+)/);
  const oldTokens = tokenize(oldLine);
  const newTokens = tokenize(newLine);

  const m = oldTokens.length;
  const n = newTokens.length;

  // Matriz LCS para tokens
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        oldTokens[i - 1] === newTokens[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtracking
  const oldResult: Array<{ text: string; changed: boolean }> = [];
  const newResult: Array<{ text: string; changed: boolean }> = [];

  let i = m;
  let j = n;
  const steps: Array<{ kind: 'equal' | 'removed' | 'added'; text: string }> = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
      steps.push({ kind: 'equal', text: oldTokens[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      steps.push({ kind: 'added', text: newTokens[j - 1] });
      j--;
    } else {
      steps.push({ kind: 'removed', text: oldTokens[i - 1] });
      i--;
    }
  }
  steps.reverse();

  for (const step of steps) {
    if (step.kind === 'equal') {
      oldResult.push({ text: step.text, changed: false });
      newResult.push({ text: step.text, changed: false });
    } else if (step.kind === 'removed') {
      oldResult.push({ text: step.text, changed: true });
    } else {
      newResult.push({ text: step.text, changed: true });
    }
  }

  return { old: oldResult, new: newResult };
}

/**
 * Tipos para o diff visual side-by-side (Comparação).
 */
type SideBySideRowKind = 'equal' | 'changed' | 'only-old' | 'only-new';

interface SideBySideRow {
  kind: SideBySideRowKind;
  num: number;
  /** Tokens do lado esquerdo (versão antiga) */
  oldTokens?: Array<{ text: string; changed: boolean }>;
  /** Tokens do lado direito (versão nova) */
  newTokens?: Array<{ text: string; changed: boolean }>;
  /** Texto puro para linhas iguais (sem diff de palavras) */
  equalText?: string;
}

/**
 * Computa as linhas do diff side-by-side alinhando segmentos correspondentes.
 * Usa LCS sobre segmentos semânticos (parágrafos, itens de lista, etc.)
 * e aplica diff de palavras em cada par de linhas modificadas.
 *
 * @param oldHtml - HTML da versão antiga
 * @param newHtml - HTML da versão nova
 * @returns Array de linhas para renderização side-by-side
 */
function computeSideBySideRows(oldHtml: string, newHtml: string): SideBySideRow[] {
  const oldSegs = extractSegments(oldHtml);
  const newSegs = extractSegments(newHtml);
  const m = oldSegs.length;
  const n = newSegs.length;

  // LCS sobre segmentos
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        oldSegs[i - 1] === newSegs[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtracking
  const steps: Array<{ kind: 'equal' | 'removed' | 'added'; text: string }> = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldSegs[i - 1] === newSegs[j - 1]) {
      steps.push({ kind: 'equal', text: oldSegs[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      steps.push({ kind: 'added', text: newSegs[j - 1] });
      j--;
    } else {
      steps.push({ kind: 'removed', text: oldSegs[i - 1] });
      i--;
    }
  }
  steps.reverse();

  // Constrói linhas do diff side-by-side
  const rows: SideBySideRow[] = [];
  let lineNum = 1;

  let stepIdx = 0;
  while (stepIdx < steps.length) {
    const step = steps[stepIdx];

    if (step.kind === 'equal') {
      rows.push({ kind: 'equal', num: lineNum++, equalText: step.text });
      stepIdx++;
    } else if (step.kind === 'removed') {
      // Verifica se o próximo passo é 'added' (par modificado)
      if (stepIdx + 1 < steps.length && steps[stepIdx + 1].kind === 'added') {
        const diff = wordDiff(step.text, steps[stepIdx + 1].text);
        rows.push({
          kind: 'changed',
          num: lineNum++,
          oldTokens: diff.old,
          newTokens: diff.new,
        });
        stepIdx += 2;
      } else {
        // Linha removida sem correspondência
        rows.push({
          kind: 'only-old',
          num: lineNum++,
          oldTokens: [{ text: step.text, changed: true }],
        });
        stepIdx++;
      }
    } else {
      // Linha adicionada sem correspondência
      rows.push({
        kind: 'only-new',
        num: lineNum++,
        newTokens: [{ text: step.text, changed: true }],
      });
      stepIdx++;
    }
  }

  return rows;
}

/**
 * Algoritmo LCS (Longest Common Subsequence) para diff linha-a-linha.
 * Opera sobre texto puro (sem HTML) para exibir diferenças legíveis.
 *
 * @param oldText - Texto da versão antiga
 * @param newText - Texto da versão atual
 * @returns Array de DiffLine com tipo e conteúdo de cada linha
 */
function computeDiff(oldText: string, newText: string): DiffLine[] {
  // Converte para texto puro antes de calcular diff
  const oldLines = htmlToPlainText(oldText).split('\n').filter(Boolean);
  const newLines = htmlToPlainText(newText).split('\n').filter(Boolean);

  const m = oldLines.length;
  const n = newLines.length;

  // Matriz LCS
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtracking para reconstruir o diff
  const steps: DiffLine[] = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      steps.push({ type: 'equal', text: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      steps.push({ type: 'added', text: newLines[j - 1] });
      j--;
    } else {
      steps.push({ type: 'removed', text: oldLines[i - 1] });
      i--;
    }
  }

  return steps.reverse();
}

/**
 * Agrupa linhas do diff em blocos de contexto (iguais) e mudança (added/removed).
 * Linhas de contexto entre blocos de mudança são colapsadas automaticamente.
 * Sempre mostra CONTEXT_LINES linhas antes e depois de cada bloco de mudança.
 */
function groupDiffIntoBlocks(diffLines: DiffLine[]): DiffBlock[] {
  const blocks: DiffBlock[] = [];
  let equalBuffer: string[] = [];

  const flushEqual = (isLast: boolean) => {
    if (equalBuffer.length === 0) return;

    if (blocks.length === 0 && isLast) {
      // Documento sem mudanças — coloca tudo como contexto colapsado
      blocks.push({ kind: 'context', lines: equalBuffer, expanded: false });
    } else if (blocks.length === 0) {
      // Contexto inicial: mostra só as últimas CONTEXT_LINES linhas
      const tail = equalBuffer.slice(-CONTEXT_LINES);
      if (equalBuffer.length > CONTEXT_LINES) {
        blocks.push({
          kind: 'context',
          lines: equalBuffer.slice(0, equalBuffer.length - CONTEXT_LINES),
          expanded: false,
        });
      }
      if (tail.length > 0) {
        // Contexto pré-mudança: não colapsa
        blocks.push({ kind: 'context', lines: tail, expanded: true });
      }
    } else if (isLast) {
      // Contexto final: mostra só as primeiras CONTEXT_LINES linhas
      const head = equalBuffer.slice(0, CONTEXT_LINES);
      if (head.length > 0) {
        blocks.push({ kind: 'context', lines: head, expanded: true });
      }
      if (equalBuffer.length > CONTEXT_LINES) {
        blocks.push({
          kind: 'context',
          lines: equalBuffer.slice(CONTEXT_LINES),
          expanded: false,
        });
      }
    } else {
      // Contexto entre dois blocos de mudança
      const before = equalBuffer.slice(0, CONTEXT_LINES);
      const after = equalBuffer.slice(-CONTEXT_LINES);
      const middle = equalBuffer.slice(CONTEXT_LINES, equalBuffer.length - CONTEXT_LINES);
      if (before.length > 0) {
        blocks.push({ kind: 'context', lines: before, expanded: true });
      }
      if (middle.length > 0) {
        blocks.push({ kind: 'context', lines: middle, expanded: false });
      }
      if (after.length > 0) {
        blocks.push({ kind: 'context', lines: after, expanded: true });
      }
    }
    equalBuffer = [];
  };

  let removed: string[] = [];
  let added: string[] = [];

  const flushChange = () => {
    if (removed.length > 0 || added.length > 0) {
      blocks.push({ kind: 'change', removed, added });
      removed = [];
      added = [];
    }
  };

  for (const line of diffLines) {
    if (line.type === 'equal') {
      flushChange();
      equalBuffer.push(line.text);
    } else if (line.type === 'removed') {
      flushEqual(false);
      removed.push(line.text);
    } else {
      // added — pode seguir após removed
      flushEqual(false);
      added.push(line.text);
    }
  }

  // Finaliza buffers
  flushChange();
  flushEqual(true);

  return blocks;
}

// ============================================================================
// Sub-componente: Item de versão na lista lateral
// ============================================================================

interface VersionItemProps {
  version: ContractTemplateVersion;
  isSelected: boolean;
  isLatest: boolean;
  displayLabel: string;
  onClick: () => void;
}

/**
 * Botão de item de versão na barra lateral.
 * Destaca visualmente a versão selecionada e a mais recente.
 */
function VersionItem({ version, isSelected, isLatest, displayLabel, onClick }: VersionItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-3 rounded-lg border transition-all',
        isSelected
          ? 'border-primary bg-primary/8 shadow-sm'
          : 'border-transparent hover:bg-muted/60 hover:border-border',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('font-mono text-sm font-semibold', isSelected && 'text-primary')}>
          {displayLabel}
        </span>
        {isLatest && (
          <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4">
            atual
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-0.5">
        {format(new Date(version.created_at), "dd/MM/yy 'às' HH:mm", { locale: ptBR })}
      </p>
    </button>
  );
}

// ============================================================================
// Sub-componente: Comparação diff visual side-by-side estilo GitHub/VS Code
// ============================================================================

interface DiffSideBySideProps {
  oldVersion: ContractTemplateVersion;
  newVersion: ContractTemplateVersion;
  oldLabel: string;
  newLabel: string;
}

/**
 * Componente de diff visual de alta fidelidade, estilo GitHub/VS Code.
 *
 * Funcionalidades:
 * - Extrai segmentos semânticos (p, li, h1-h6) em vez de linhas brutas
 * - Alinha parágrafos correspondentes lado a lado via LCS
 * - Aplica highlight de fundo vermelho/verde por linha modificada
 * - Highlight intra-linha (por palavras) com cor mais saturada
 * - Linhas sem par (só removida ou só adicionada) mostram célula vazia no outro lado
 * - Numeração de blocos para referência rápida
 * - Scroll sincronizado entre os dois painéis
 *
 * @param oldVersion - Versão antiga para o lado esquerdo
 * @param newVersion - Versão nova para o lado direito
 * @param oldLabel   - Rótulo da versão antiga (ex: "v1.0")
 * @param newLabel   - Rótulo da versão nova (ex: "v1.1 (atual)")
 */
function DiffSideBySide({ oldVersion, newVersion, oldLabel, newLabel }: DiffSideBySideProps) {
  // Calcula as linhas do diff side-by-side
  const rows = useMemo(
    () => computeSideBySideRows(oldVersion.content_html || '', newVersion.content_html || ''),
    [oldVersion.content_html, newVersion.content_html],
  );

  // Contadores de mudanças
  const addedCount = rows.filter((r) => r.kind === 'only-new' || r.kind === 'changed').length;
  const removedCount = rows.filter((r) => r.kind === 'only-old' || r.kind === 'changed').length;
  const hasChanges = rows.some((r) => r.kind !== 'equal');

  // Refs para sincronizar scroll
  const scrollRef = useRef<HTMLDivElement>(null);

  // Estado de versões idênticas
  if (!hasChanges) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-center py-16 px-8">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <History className="h-8 w-8 text-muted-foreground/40" />
        </div>
        <h3 className="text-sm font-semibold mb-1">Versões idênticas</h3>
        <p className="text-xs text-muted-foreground max-w-xs">
          Não foram detectadas diferenças de conteúdo entre as versões.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── Barra de cabeçalho com os labels e contadores ── */}
      <div className="flex shrink-0 border-b">
        {/* Label versão antiga */}
        <div className="flex-1 flex items-center gap-2 px-4 py-2 border-r" style={{ background: 'rgba(239,68,68,0.06)' }}>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: '#ef4444' }} />
          <span className="text-xs font-semibold font-mono" style={{ color: '#dc2626' }}>{oldLabel}</span>
          <span className="text-[11px] text-muted-foreground ml-1">
            {format(new Date(oldVersion.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
          </span>
          {removedCount > 0 && (
            <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#dc2626' }}>
              <Minus className="h-3 w-3" />
              {removedCount}
            </span>
          )}
        </div>
        {/* Label versão nova */}
        <div className="flex-1 flex items-center gap-2 px-4 py-2" style={{ background: 'rgba(34,197,94,0.06)' }}>
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: '#22c55e' }} />
          <span className="text-xs font-semibold font-mono" style={{ color: '#16a34a' }}>{newLabel}</span>
          <span className="text-[11px] text-muted-foreground ml-1">
            {format(new Date(newVersion.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
          </span>
          {addedCount > 0 && (
            <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#16a34a' }}>
              <Plus className="h-3 w-3" />
              {addedCount}
            </span>
          )}
        </div>
      </div>

      {/* ── Tabela de diff ── */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-xs font-mono" style={{ tableLayout: 'fixed' }}>
          {/* Cabeçalho das colunas */}
          <colgroup>
            <col style={{ width: '2rem' }} />  {/* # linha esquerda */}
            <col style={{ width: '50%' }} />   {/* conteúdo esquerda */}
            <col style={{ width: '2rem' }} />  {/* # linha direita */}
            <col style={{ width: '50%' }} />   {/* conteúdo direita */}
          </colgroup>
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-center text-[10px] text-muted-foreground py-1 font-normal border-r select-none">#</th>
              <th className="text-left text-[10px] text-muted-foreground py-1 px-3 font-semibold border-r">{oldLabel}</th>
              <th className="text-center text-[10px] text-muted-foreground py-1 font-normal border-r select-none">#</th>
              <th className="text-left text-[10px] text-muted-foreground py-1 px-3 font-semibold">{newLabel}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              /* ── Linha igual: fundo neutro, mesmo texto nos dois lados ── */
              if (row.kind === 'equal') {
                return (
                  <tr key={idx} className="border-b border-muted/40 hover:bg-muted/20">
                    <td className="text-center text-muted-foreground/40 py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px' }}>{row.num}</td>
                    <td className="py-1.5 px-3 border-r text-foreground/70 break-words whitespace-pre-wrap align-top">{row.equalText}</td>
                    <td className="text-center text-muted-foreground/40 py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px' }}>{row.num}</td>
                    <td className="py-1.5 px-3 text-foreground/70 break-words whitespace-pre-wrap align-top">{row.equalText}</td>
                  </tr>
                );
              }

              /* ── Linha modificada: vermelho à esquerda, verde à direita, highlight por palavras ── */
              if (row.kind === 'changed') {
                return (
                  <tr key={idx} className="border-b border-muted/40">
                    {/* Célula esquerda (removida) */}
                    <td className="text-center py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px', background: 'rgba(239,68,68,0.10)', color: '#dc2626', borderLeft: '3px solid #ef4444' }}>{row.num}</td>
                    <td className="py-1.5 px-3 border-r break-words whitespace-pre-wrap align-top" style={{ background: 'rgba(239,68,68,0.08)' }}>
                      {row.oldTokens?.map((token, ti) =>
                        token.changed ? (
                          <mark key={ti} style={{ background: 'rgba(239,68,68,0.30)', color: '#b91c1c', borderRadius: '2px', padding: '0 1px' }}>{token.text}</mark>
                        ) : (
                          <span key={ti} style={{ color: '#7f1d1d' }}>{token.text}</span>
                        ),
                      )}
                    </td>
                    {/* Célula direita (adicionada) */}
                    <td className="text-center py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px', background: 'rgba(34,197,94,0.10)', color: '#16a34a', borderLeft: '3px solid #22c55e' }}>{row.num}</td>
                    <td className="py-1.5 px-3 break-words whitespace-pre-wrap align-top" style={{ background: 'rgba(34,197,94,0.08)' }}>
                      {row.newTokens?.map((token, ti) =>
                        token.changed ? (
                          <mark key={ti} style={{ background: 'rgba(34,197,94,0.35)', color: '#14532d', borderRadius: '2px', padding: '0 1px' }}>{token.text}</mark>
                        ) : (
                          <span key={ti} style={{ color: '#14532d' }}>{token.text}</span>
                        ),
                      )}
                    </td>
                  </tr>
                );
              }

              /* ── Só removida: vermelho à esquerda, célula vazia com fundo leve à direita ── */
              if (row.kind === 'only-old') {
                return (
                  <tr key={idx} className="border-b border-muted/40">
                    <td className="text-center py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px', background: 'rgba(239,68,68,0.10)', color: '#dc2626', borderLeft: '3px solid #ef4444' }}>{row.num}</td>
                    <td className="py-1.5 px-3 border-r break-words whitespace-pre-wrap align-top" style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>
                      <span className="mr-1.5 font-bold">−</span>
                      {row.oldTokens?.[0]?.text}
                    </td>
                    <td className="border-r" style={{ background: 'rgba(239,68,68,0.03)' }} />
                    <td style={{ background: 'rgba(239,68,68,0.03)' }} />
                  </tr>
                );
              }

              /* ── Só adicionada: célula vazia com fundo leve à esquerda, verde à direita ── */
              return (
                <tr key={idx} className="border-b border-muted/40">
                  <td className="border-r" style={{ background: 'rgba(34,197,94,0.03)' }} />
                  <td className="border-r" style={{ background: 'rgba(34,197,94,0.03)' }} />
                  <td className="text-center py-1.5 px-1 border-r select-none align-top" style={{ fontSize: '10px', background: 'rgba(34,197,94,0.10)', color: '#16a34a', borderLeft: '3px solid #22c55e' }}>{row.num}</td>
                  <td className="py-1.5 px-3 break-words whitespace-pre-wrap align-top" style={{ background: 'rgba(34,197,94,0.08)', color: '#14532d' }}>
                    <span className="mr-1.5 font-bold">+</span>
                    {row.newTokens?.[0]?.text}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-componente: Visualização do Diff (texto puro com contexto colapsável)
// ============================================================================

interface DiffViewProps {
  selectedVersion: ContractTemplateVersion;
  latestVersion: ContractTemplateVersion;
  selectedLabel: string;
  latestLabel: string;
}

/**
 * Exibe o diff entre duas versões usando texto puro (sem HTML).
 * Linhas de contexto distantes das mudanças são colapsadas com botão expandir.
 */
function DiffView({ selectedVersion, latestVersion, selectedLabel, latestLabel }: DiffViewProps) {
  // Calcula linhas do diff
  const diffLines = useMemo(() => {
    logger.debug('Calculando diff de texto puro entre versões', {
      from: selectedVersion.version_number,
      to: latestVersion.version_number,
    });
    return computeDiff(
      selectedVersion.content_html || '',
      latestVersion.content_html || '',
    );
  }, [selectedVersion.content_html, latestVersion.content_html]);

  // Agrupa em blocos (contexto colapsável + mudanças)
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});

  const blocks = useMemo(() => groupDiffIntoBlocks(diffLines), [diffLines]);

  // Contadores
  const addedCount = diffLines.filter((l) => l.type === 'added').length;
  const removedCount = diffLines.filter((l) => l.type === 'removed').length;

  const toggleExpand = (idx: number) =>
    setExpandedMap((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Sem mudanças
  if (addedCount === 0 && removedCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 text-center py-16 px-8">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <History className="h-8 w-8 text-muted-foreground/40" />
        </div>
        <h3 className="text-sm font-semibold mb-1">Versões idênticas</h3>
        <p className="text-xs text-muted-foreground max-w-xs">
          O conteúdo desta versão é idêntico à versão atual. Não há diferenças para exibir.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── Barra de resumo das mudanças ── */}
      <div className="flex items-center gap-4 px-4 py-2.5 border-b bg-muted/20 shrink-0">
        <span className="text-xs text-muted-foreground font-mono font-medium">
          {selectedLabel} → {latestLabel}
        </span>
        <div className="flex items-center gap-3 ml-auto">
          <span className="flex items-center gap-1 text-xs font-semibold text-[hsl(var(--chart-2))]">
            <Plus className="h-3 w-3" />
            {addedCount} adicionada{addedCount !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-destructive">
            <Minus className="h-3 w-3" />
            {removedCount} removida{removedCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ── Blocos do diff ── */}
      <ScrollArea className="flex-1">
        <div className="py-2">
          {blocks.map((block, idx) => {
            if (block.kind === 'context') {
              const isExpanded = block.expanded || !!expandedMap[idx];
              if (isExpanded) {
                return (
                  <div key={idx}>
                    {block.lines.map((line, li) => (
                      <div
                        key={li}
                        className="flex px-4 py-0.5 text-muted-foreground/60"
                      >
                        <span className="w-5 shrink-0 text-muted-foreground/30 select-none text-xs font-mono"> </span>
                        <span className="text-xs font-mono break-all whitespace-pre-wrap">{line}</span>
                      </div>
                    ))}
                  </div>
                );
              }
              // Bloco colapsado — exibe separador clicável
              return (
                <Button
                  key={idx}
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleExpand(idx)}
                  className="w-full flex items-center gap-2 justify-center text-xs text-muted-foreground/60 py-1 h-auto my-1 hover:bg-muted/40 rounded-none border-y border-dashed border-muted-foreground/20"
                >
                  <ChevronDown className="h-3 w-3" />
                  <span>... {block.lines.length} linha{block.lines.length !== 1 ? 's' : ''} sem alteração</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              );
            }

            // Bloco de mudança: removidas primeiro (vermelho), depois adicionadas (verde)
            return (
              <div key={idx} className="border-y border-dashed border-muted/60 my-0.5">
                {block.removed.map((line, li) => (
                  <div
                    key={`r-${li}`}
                    className="flex items-start px-4 py-1 border-l-4"
                    style={{ background: 'rgba(239,68,68,0.12)', borderLeftColor: '#ef4444' }}
                  >
                    <span className="w-5 shrink-0 select-none text-xs font-mono font-bold" style={{ color: '#dc2626' }}>−</span>
                    <span className="text-xs font-mono break-all whitespace-pre-wrap" style={{ color: '#b91c1c' }}>{line}</span>
                  </div>
                ))}
                {block.added.map((line, li) => (
                  <div
                    key={`a-${li}`}
                    className="flex items-start px-4 py-1 border-l-4"
                    style={{ background: 'rgba(34,197,94,0.12)', borderLeftColor: '#22c55e' }}
                  >
                    <span className="w-5 shrink-0 select-none text-xs font-mono font-bold" style={{ color: '#16a34a' }}>+</span>
                    <span className="text-xs font-mono break-all whitespace-pre-wrap" style={{ color: '#14532d' }}>{line}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

// ============================================================================
// Componente principal
// ============================================================================

export function ContractTemplateVersionHistoryDialog({ open, onClose, templateId, templateName }: Props) {
  const queryClient = useQueryClient();

  // Ao abrir o modal, invalida caches de versões e do estado atual do template
  // para garantir que a última versão recém-salva apareça sem precisar de F5.
  useEffect(() => {
    if (open && templateId) {
      queryClient.invalidateQueries({ queryKey: ['contract-template-versions', templateId] });
      queryClient.invalidateQueries({ queryKey: ['contract-template-current', templateId] });
    }
  }, [open, templateId, queryClient]);

  // Busca versões do template (só quando o modal está aberto)
  const { data: versions = [], isLoading } = useContractTemplateVersions(open ? templateId : undefined);

  // ── Query: estado ATUAL do template via RPC server-side (get_contract_template_current) ──
  // O trigger salva o estado ANTERIOR (OLD) a cada update, então o estado atual
  // só existe em contract_templates e precisa ser consultado separadamente.
  // RELEASE v2.0.0 - 2026-05-10: migrado para RPC, sem acesso direto à tabela
  const { data: currentTemplate, isLoading: isLoadingCurrent } = useQuery({
    queryKey: ['contract-template-current', templateId],
    queryFn: async () => {
      logger.debug('Buscando estado atual do template para histórico (RPC)', { templateId });
      const { data, error } = await supabase.rpc('get_contract_template_current', {
        p_template_id: templateId,
      });

      if (error) {
        logger.error('Erro ao buscar template atual', { error });
        throw error;
      }
      // RPC retorna SETOF (1 linha) — pega a primeira
      return Array.isArray(data) ? data[0] : data;
    },
    enabled: open && !!templateId,
  });

  // ── Constrói versão virtual "atual" a partir do template live ──
  // Essa versão representa o estado REAL salvo no template, que o trigger ainda não snapshotou
  const allVersions = useMemo(() => {
    if (!currentTemplate || versions.length === 0) return versions;

    const maxVersionNumber = versions[0]?.version_number ?? 0;

    const liveVersion: ContractTemplateVersion = {
      id: 'current-live',
      tenant_id: '',
      template_id: templateId,
      version_number: maxVersionNumber + 1,
      content_html: currentTemplate.content_html,
      variables: currentTemplate.variables as any,
      background_image_path: currentTemplate.background_image_path,
      bg_margin_top_mm: currentTemplate.bg_margin_top_mm,
      bg_margin_bottom_mm: currentTemplate.bg_margin_bottom_mm,
      show_page_numbers: currentTemplate.show_page_numbers,
      page_number_position: currentTemplate.page_number_position,
      pagination_y_offset_mm: currentTemplate.pagination_y_offset_mm,
      base_pdf_path: currentTemplate.base_pdf_path,
      cover_pdf_path: currentTemplate.cover_pdf_path,
      base_pdf_position: currentTemplate.base_pdf_position,
      created_at: currentTemplate.updated_at,
      created_by: null,
      change_note: null,
    };

    return [liveVersion, ...versions];
  }, [currentTemplate, versions, templateId]);

  // Versão selecionada para comparação (ID explícito selecionado pelo usuário)
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Loading combinado
  const loading = isLoading || isLoadingCurrent;

  // Versão mais recente = versão virtual "atual" (primeiro item)
  const latestVersion = allVersions[0] ?? null;

  // Resolve a versão selecionada (padrão: penúltima, para comparar com a atual)
  const selectedVersion = useMemo(() => {
    if (!selectedVersionId) {
      return allVersions.length > 1 ? allVersions[1] : null;
    }
    return allVersions.find((v) => v.id === selectedVersionId) ?? null;
  }, [selectedVersionId, allVersions]);

  /**
   * Formata o label de exibição da versão.
   * version_number 1 = v1.0, 2 = v1.1, etc.
   */
  const getVersionLabel = (v: ContractTemplateVersion) => `v1.${v.version_number - 1}`;

  // ── Contagem de mudanças para exibir no badge do header ──
  const changeCount = useMemo(() => {
    if (!selectedVersion || !latestVersion || selectedVersion.id === latestVersion.id) return 0;
    const lines = computeDiff(
      selectedVersion.content_html || '',
      latestVersion.content_html || '',
    );
    return lines.filter((l) => l.type !== 'equal').length;
  }, [selectedVersion, latestVersion]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-full h-[88vh] flex flex-col p-0 gap-0 overflow-hidden">

        {/* ── Cabeçalho ── */}
        <DialogHeader className="px-5 py-3 border-b shrink-0">
          <div className="flex items-center gap-2.5">
            <History className="h-4 w-4 text-primary shrink-0" />
            <DialogTitle className="text-sm font-semibold leading-tight">
              Histórico de Versões — <span className="text-primary">{templateName}</span>
            </DialogTitle>
            {changeCount > 0 && (
              <Badge variant="secondary" className="text-[11px] px-2 py-0 h-5 ml-1">
                {changeCount} alteraç{changeCount !== 1 ? 'ões' : 'ão'}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* ── Corpo: lista lateral + painel principal ── */}
        <div className="flex flex-1 min-h-0">

          {/* ── Painel lateral: lista de versões ── */}
          <div className="w-48 shrink-0 border-r flex flex-col bg-muted/10">
            <div className="px-3 py-2 border-b">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                Versões
              </p>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-lg" />
                  ))
                ) : allVersions.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-2 py-4 text-center">
                    Nenhuma versão registrada.
                  </p>
                ) : (
                  allVersions.map((v, idx) => {
                    const isLatest = idx === 0;
                    const isSelected = selectedVersion?.id === v.id;
                    return (
                      <VersionItem
                        key={v.id}
                        version={v}
                        isSelected={isSelected}
                        isLatest={isLatest}
                        displayLabel={getVersionLabel(v)}
                        onClick={() => {
                          // Clique na versão atual não faz comparação
                          if (!isLatest) setSelectedVersionId(v.id);
                        }}
                      />
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          {/* ── Painel principal: tabs de preview e diff ── */}
          <div className="flex-1 min-w-0 flex flex-col">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-4 w-56" />
                </div>
              </div>
            ) : allVersions.length <= 1 ? (
              /* Apenas uma versão — sem histórico */
              <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-8">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <History className="h-8 w-8 text-muted-foreground/30" />
                </div>
                <h3 className="text-sm font-semibold mb-1">Nenhuma versão anterior registrada</h3>
                <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
                  O histórico é criado automaticamente a cada edição salva do modelo.
                  Edite e salve para criar a próxima versão.
                </p>
              </div>
            ) : !latestVersion || !selectedVersion ? (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                Selecione uma versão anterior para comparar.
              </div>
            ) : selectedVersion.id === latestVersion.id ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-8">
                <p className="text-sm text-muted-foreground">
                  Esta é a versão atual. Selecione uma versão anterior na lista para ver as diferenças.
                </p>
              </div>
            ) : (
              /* ── Comparação visual side-by-side (estilo GitHub/VS Code) ── */
              <DiffSideBySide
                oldVersion={selectedVersion}
                newVersion={latestVersion}
                oldLabel={getVersionLabel(selectedVersion)}
                newLabel={`${getVersionLabel(latestVersion)} (atual)`}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
